/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, EventEmitter, inject} from '@angular/core';
import {ToastService} from '@siemens/ix-angular';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {User} from '../../../../models/User';
import {UserService} from '../../../../services/user.service';
import {UserManagementService} from '../../../../services/user-management.service';

const HTTP_CONFLICT = 409;
const HTTP_INTERNAL_ERROR = 500;

@Component({
  selector: 'app-invite-user',
  templateUrl: './invite-user.component.html',
  styleUrl: './invite-user.component.scss',
  standalone: false,
})
export class InviteUserComponent {
  private readonly userService = inject(UserService);
  private readonly userManagementService = inject(UserManagementService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  targetRoute = 'app/list-user';
  loading = false;
  cancelEvent = new EventEmitter();

  async inviteUser(userData: User) {
    this.loading = true;
    const inviteUserObject = {
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      roles: userData.roles,
    };
    await this.userManagementService
      .inviteUser(inviteUserObject)
      .then(async () => {
        const msg = this.translate.instant('um.user_invite_successful');
        await this.toastService.show({message: msg, type: 'success'});
        await this.router.navigate([this.targetRoute]);
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        if (error.status === HTTP_CONFLICT) {
          const msg = this.translate.instant('um.user_invite_already_there', {
            invitedUser: userData.email,
          });
          this.toastService.show({message: msg, type: 'error'});
        } else if (
          error.error.status === HTTP_INTERNAL_ERROR &&
          error.error.message.includes(
            "Bad request: creating accounts with company's federated domains not allowed",
          )
        ) {
          const msg = this.translate.instant('um.user_not_matching', {
            user: userData.email,
          });
          this.toastService.show({message: msg, type: 'error'});
        } else if (error.error.message[0].includes('email must be an email')) {
          const msg = this.translate.instant('um.email_not_valid');
          this.toastService.show({message: msg, type: 'error'});
        } else {
          throw Error(error);
        }
      });
  }

  async onBackButtonClick() {
    this.cancelEvent.emit();
  }
}
